import { useCallback, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { dayChange, weekChange } from "../../actions";
import {
    getTodayDate,
    getPreviousWeek,
    getNextWeek,
    getWeekDayDate,
    getDay,
} from "../../utils/date";
import { getCurrentDay } from "../../state/currentDay.selectors";
import { getWeekVisible } from "../../state/weekVisible.selectors";
import { ArrowLeft, ArrowRight } from "../generic/ArrowIcons";
import { h } from "../../h";

type WeekDayStyledProps = {
    isPast: boolean;
    isToday: boolean;
    isPlanned: boolean;
    isCurrent: boolean;
};

const WeekDayStyled = styled.div<WeekDayStyledProps>`
    opacity: ${({ isPast }) => (isPast ? `0.4` : `1`)};
    color: ${({ isToday, theme }) =>
        isToday ? theme.colorAccent : theme.colorMain};
    weight: ${({ isPlanned }) => (isPlanned ? `bold` : `normal`)};
    font-size: ${({ isCurrent, theme }) =>
        isCurrent ? theme.fontL : theme.fontM};
    cursor: pointer;
`;

const WeekContainerStyled = styled.div`
    display: flex;
    align-items: center;
    height: 100%;

    & > * {
        flex: 1 1 0;
        text-align: center;
    }
`;

export const Week = (): ReactElement => {
    const dispatch = useDispatch();
    const weekVisible = useSelector(getWeekVisible);
    const onPreviousWeekClick = useCallback(() => {
        dispatch(weekChange(getPreviousWeek(weekVisible)));
    }, [dispatch, weekVisible]);
    const onNextWeekClick = useCallback(() => {
        dispatch(weekChange(getNextWeek(weekVisible)));
    }, [dispatch, weekVisible]);
    const currentDay = useSelector(getCurrentDay);
    const onDateClick = useCallback(
        (date) => {
            dispatch(dayChange(date));
        },
        [dispatch],
    );
    return h(
        WeekContainerStyled,
        {},
        h(
            `div`,
            {
                onClick: onPreviousWeekClick,
            },
            h(ArrowLeft),
        ),
        [1, 2, 3, 4, 5, 6, 7].map((day) => {
            const date = getWeekDayDate(day)(weekVisible);
            const today = getTodayDate();
            return h(
                WeekDayStyled,
                {
                    key: date,
                    onClick: () => onDateClick(date),
                    isPast: date < today,
                    isToday: date === today,
                    isPlanned: false,
                    isCurrent: date === currentDay,
                },
                getDay(date),
            );
        }),
        h(
            `div`,
            {
                onClick: onNextWeekClick,
            },
            h(ArrowRight),
        ),
    );
};
